'use client';

import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { Button } from 'ui/Button';
import { IRootStore } from 'store/Root.reducer';
import { PAGES } from 'utils/link';
import { getCurrentUserRequest } from 'store/Auth/Auth.action';
import errorIcon from 'assets/images/icons/error.svg';

interface ErrorTexts {
  [key: string]: {
    title: string;
    message: string;
  };
}

const errorTexts: ErrorTexts = {
  '400': {
    title: 'Error 400',
    message: 'Bad request. Please check your input and try again.',
  },
  '401': {
    title: 'Error 401',
    message: 'Unauthorized access. Please log in to continue.',
  },
  '403': {
    title: 'Error 403',
    message: 'Access is limited. Unfortunately, you do not have permission to view this section.',
  },
  '404': {
    title: 'Error 404',
    message: 'Oops! This page doesn\'t exist. Check the address or return to the main page.',
  },
  '500': {
    title: 'Error 500',
    message: 'Something went wrong. Please try refreshing the page later.',
  },
};

const getErrorInfo = (errorCode?: string) => {
  if (!errorCode) {
    return errorTexts['404'];
  }

  const code = parseInt(errorCode, 10);
  if (isNaN(code) || code < 400 || code >= 600) {
    return errorTexts['404'];
  }

  if (errorTexts[errorCode]) {
    return errorTexts[errorCode];
  }

  if (code >= 400 && code < 500) {
    return {
      title: `Error ${code}`,
      message: 'Oops! This page doesn\'t exist. Check the address or return to the main page.',
    };
  }

  if (code >= 500 && code < 600) {
    return {
      title: `Error ${code}`,
      message: 'Something went wrong. Please try refreshing the page later.',
    };
  }

  return errorTexts['404'];
};

export const ErrorPage: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading } = useSelector((state: IRootStore) => state.auth);
  const token = localStorage.getItem('access_token');

  useEffect(() => {
    if (token && !user && !loading) {
      dispatch(getCurrentUserRequest() as any);
    }
  }, [token, user, loading, dispatch]);

  const errorCode = location.pathname.replace('/', '');
  const errorInfo = getErrorInfo(errorCode);

  const handleGoHome = () => {
    if (user) {
      if (user.role === 'admin') {
        navigate(PAGES.ADMIN_USERS);
      } else {
        navigate(PAGES.DASHBOARD.replace(':id', user.id.toString()));
      }
    } else {
      navigate(PAGES.AUTH_LOGIN);
    }
  };

  return (
    <div className="error-page">
      <img src={errorIcon} alt="Error icon" className="error-page__icon"/>
      <h1 className="error-page__code">Error {errorCode}</h1>
      <div className="error-page__message-container">
        <p className="error-page__message">{errorInfo.message}</p>
      </div>
      <Button onClick={handleGoHome}>Main page</Button>
    </div>
  );
};